import React, { useState, useEffect, useRef, useMemo } from "react";
import { Truck, ChevronDown, ChevronUp, Check } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LoadDetailsPanel } from "./LoadDetailsPanel";
import { haversineDistance, type GeoCoords, type DatabaseLoad } from "@/lib/loadSearch";
import { formatEquipmentType } from "@/lib/equipmentTypes";
import { formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";
import { formatPhoneNumber } from "@/lib/formatPhone";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import type { SortOption } from "./ActiveSearchTabs";
import { displayCompanyName } from "@/lib/displayCompanyName";

/* ─── Sort config ────────────────────────────────────────────────── */

interface SortDef {
  label: string;
  shortLabel: string;
}

const SORT_GROUPS: { heading: string; options: { key: SortOption; def: SortDef }[] }[] = [
  {
    heading: "Recommended",
    options: [
      { key: "age_newest", def: { label: "Age — Newest", shortLabel: "Age — Newest" } },
      { key: "rate_highest", def: { label: "Rate — Highest", shortLabel: "Rate — Highest" } },
      { key: "rate_per_mile_highest", def: { label: "Rate/Mile — Highest", shortLabel: "Rate/Mile — Highest" } },
      { key: "pickup_soonest", def: { label: "Pickup — Soonest", shortLabel: "Pickup — Soonest" } },
    ],
  },
  {
    heading: "Distance",
    options: [
      { key: "trip_shortest", def: { label: "Trip — Shortest", shortLabel: "Trip — Shortest" } },
      { key: "trip_longest", def: { label: "Trip — Longest", shortLabel: "Trip — Longest" } },
      { key: "dh_o_shortest", def: { label: "DH Origin — Shortest", shortLabel: "DH-O — Shortest" } },
      { key: "dh_d_shortest", def: { label: "DH Dest — Shortest", shortLabel: "DH-D — Shortest" } },
    ],
  },
  {
    heading: "Load Details",
    options: [
      { key: "weight_heaviest", def: { label: "Weight — Heaviest", shortLabel: "Weight — Heaviest" } },
      { key: "weight_lightest", def: { label: "Weight — Lightest", shortLabel: "Weight — Lightest" } },
      { key: "length_longest", def: { label: "Length — Longest", shortLabel: "Length — Longest" } },
      { key: "length_shortest", def: { label: "Length — Shortest", shortLabel: "Length — Shortest" } },
      { key: "capacity_highest", def: { label: "Capacity — Highest", shortLabel: "Cap — Highest" } },
      { key: "capacity_lowest", def: { label: "Capacity — Lowest", shortLabel: "Cap — Lowest" } },
      { key: "picks_most", def: { label: "Picks — Most", shortLabel: "Picks — Most" } },
      { key: "picks_fewest", def: { label: "Picks — Fewest", shortLabel: "Picks — Fewest" } },
      { key: "eq_az", def: { label: "Equipment — A→Z", shortLabel: "EQ — A→Z" } },
      { key: "eq_za", def: { label: "Equipment — Z→A", shortLabel: "EQ — Z→A" } },
    ],
  },
  {
    heading: "Source",
    options: [
      { key: "market_az", def: { label: "Market — A→Z", shortLabel: "Mkt — A→Z" } },
      { key: "market_za", def: { label: "Market — Z→A", shortLabel: "Mkt — Z→A" } },
      { key: "company_az", def: { label: "Company — A→Z", shortLabel: "Company — A→Z" } },
      { key: "company_za", def: { label: "Company — Z→A", shortLabel: "Company — Z→A" } },
    ],
  },
];

const SORT_LABEL_MAP: Record<SortOption, string> = {} as any;
for (const g of SORT_GROUPS) for (const o of g.options) SORT_LABEL_MAP[o.key] = o.def.shortLabel;

/* ─── Capacity ranking ───────────────────────────────────────────── */

const CAPACITY_RANK: Record<string, number> = {
  FTL: 3, Full: 3, full: 3,
  Partial: 2, partial: 2,
  LTL: 1, ltl: 1,
};

function getCapacityRank(loadType: string | null | undefined): number {
  if (!loadType) return 0;
  return CAPACITY_RANK[loadType] ?? 0;
}

/* ─── Props ──────────────────────────────────────────────────────── */

interface SearchResultsTableProps {
  loads: DatabaseLoad[];
  isLoading?: boolean;
  fadingLoadIds?: Map<string, string>;
  newLoadIds?: Set<string>;
  searchOriginCoords?: GeoCoords | null;
  searchDestCoords?: GeoCoords | null;
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
  scrollToLoadId?: string | null;
  externalExpandedLoadId?: string | null;
  onExternalExpandedLoadChange?: (id: string | null) => void;
}

/* ─── Age helpers ────────────────────────────────────────────────── */

function formatAge(postedAt: string): { text: string; mins: number } {
  const mins = Math.floor((Date.now() - new Date(postedAt).getTime()) / 60000);
  if (mins < 60) return { text: `${mins}m ago`, mins };
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return { text: m > 0 ? `${h}h ${m}m` : `${h}h ago`, mins };
}


function getAgeText(mins: number): string {
  if (mins < 60) return "text-emerald-600/50";
  if (mins < 120) return "text-amber-600/50";
  if (mins < 1440) return "text-orange-600/50";
  return "text-red-600/50";
}

/* ─── Equipment map ──────────────────────────────────────────────── */

const eqMap: Record<string, string> = {
  van: "V", reefer: "R", flatbed: "F",
  step_deck: "SD", power_only: "PO",
  box_truck: "BT", hotshot: "HS", other: "O",
};

/* ─── Status labels for fade-out badge ───────────────────────────── */

const STATUS_LABELS: Record<string, string> = {
  booked: "Booked", covered: "Covered", cancelled: "Canceled", unposted: "Unposted",
  expired: "Expired", removed: "Removed", pending_broker_confirmation: "Pending",
};

function statusBadgeLabel(status: string): string {
  return STATUS_LABELS[status] || "Removed";
}

/* ─── Stop count type ────────────────────────────────────────────── */

type StopCounts = Record<string, { pickups: number; drops: number }>;

/* ─── Column header shorthand ────────────────────────────────────── */

/* ─── Weight formatter ────────────────────────────────────────────── */

function formatWeight(lbs: number | null | undefined): string {
  if (!lbs) return "—";
  if (lbs >= 1000) return `${Math.round(lbs / 1000)}k lbs`;
  return `${lbs} lbs`;
}

/* ─── Sortable header mapping ────────────────────────────────────── */

type SortPair = [SortOption, SortOption]; // [primary, reverse]

const HEADER_SORT_MAP: Record<string, SortPair> = {
  age: ["age_newest", "age_oldest"],
  rate: ["rate_highest", "rate_lowest"],
  origin: ["origin_az", "origin_za"],
  dest: ["dest_az", "dest_za"],
  dho: ["dh_o_shortest", "dh_o_longest"],
  dhd: ["dh_d_shortest", "dh_d_longest"],
  pickup: ["pickup_soonest", "pickup_latest"],
  weight: ["weight_heaviest", "weight_lightest"],
  length: ["length_longest", "length_shortest"],
  picks: ["picks_most", "picks_fewest"],
  eq: ["eq_az", "eq_za"],
  capacity: ["capacity_highest", "capacity_lowest"],
  market: ["market_az", "market_za"],
  company: ["company_az", "company_za"],
};

/* ─── Column header shorthand ────────────────────────────────────── */

const TH = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <TableHead className={cn("px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground whitespace-nowrap", className)}>
    {children}
  </TableHead>
);

/* ─── Sortable column header ─────────────────────────────────────── */

interface SortableTHProps {
  children: React.ReactNode;
  className?: string;
  sortKey: string;
  currentSort: SortOption;
  onSort: (sort: SortOption) => void;
}

const SortableTH = ({ children, className, sortKey, currentSort, onSort }: SortableTHProps) => {
  const pair = HEADER_SORT_MAP[sortKey];
  if (!pair) return <TH className={className}>{children}</TH>;

  const [primary, reverse] = pair;
  const isActive = currentSort === primary || currentSort === reverse;
  const isReversed = currentSort === reverse;

  const handleClick = () => {
    if (!isActive) {
      onSort(primary);
    } else {
      onSort(isReversed ? primary : reverse);
    }
  };

  return (
    <TableHead
      className={cn(
        "px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap cursor-pointer select-none transition-colors",
        isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/70",
        className
      )}
      onClick={handleClick}
    >
      <span className="inline-flex items-center gap-0.5">
        {children}
        {isActive && (
          isReversed
            ? <ChevronUp className="h-2.5 w-2.5" />
            : <ChevronDown className="h-2.5 w-2.5" />
        )}
      </span>
    </TableHead>
  );
};

/* ═══════════════════════════════════════════════════════════════════ */
export function SearchResultsTable({
  loads,
  isLoading,
  fadingLoadIds,
  newLoadIds,
  searchOriginCoords,
  searchDestCoords,
  sortBy = "age_newest",
  onSortChange,
  scrollToLoadId,
  externalExpandedLoadId,
  onExternalExpandedLoadChange,
}: SearchResultsTableProps) {
  const [internalExpandedLoadId, setInternalExpandedLoadId] = useState<string | null>(null);
  const expandedLoadId = externalExpandedLoadId ?? internalExpandedLoadId;
  const setExpandedLoadId = (id: string | null) => {
    setInternalExpandedLoadId(id);
    onExternalExpandedLoadChange?.(id);
  };

  /* ── Scroll-to logic ──────────────────────────────────────────── */
  const [highlightedLoadId, setHighlightedLoadId] = useState<string | null>(null);

  useEffect(() => {
    if (!scrollToLoadId) return;
    const el = document.getElementById(`load-row-${scrollToLoadId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedLoadId(scrollToLoadId);
      setTimeout(() => setHighlightedLoadId(null), 2000);
    }
  }, [scrollToLoadId]);

  /* ── Fetch stop counts for visible loads ─────────────────────── */
  const [stopCounts, setStopCounts] = useState<StopCounts>({});
  const fetchedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!loads.length) return;
    const idsToFetch = loads.map((l) => l.id).filter((id) => !fetchedRef.current.has(id));
    if (!idsToFetch.length) return;

    (async () => {
      const { data } = await supabase
        .from("load_stops")
        .select("load_id, stop_type")
        .in("load_id", idsToFetch.slice(0, 200));

      if (!data) return;

      const counts: StopCounts = {};
      for (const row of data) {
        if (!counts[row.load_id]) counts[row.load_id] = { pickups: 0, drops: 0 };
        if (row.stop_type === "pickup") counts[row.load_id].pickups++;
        else if (row.stop_type === "delivery") counts[row.load_id].drops++;
      }

      // Mark fetched (including loads with no stops rows — intentional)
      for (const id of idsToFetch.slice(0, 200)) fetchedRef.current.add(id);

      setStopCounts((prev) => ({ ...prev, ...counts }));
    })();
  }, [loads]);

  /* ── DH calculator (numeric) ────────────────────────────────── */
  function dhoNum(load: DatabaseLoad): number {
    if (!searchOriginCoords || !load.origin_latitude || !load.origin_longitude) return Infinity;
    return haversineDistance(searchOriginCoords.lat, searchOriginCoords.lon, load.origin_latitude, load.origin_longitude);
  }

  function dhdNum(load: DatabaseLoad): number {
    if (!searchDestCoords || !load.destination_latitude || !load.destination_longitude) return Infinity;
    return haversineDistance(searchDestCoords.lat, searchDestCoords.lon, load.destination_latitude, load.destination_longitude);
  }

  function dho(load: DatabaseLoad): string | null {
    const v = dhoNum(load);
    return v === Infinity ? null : Math.round(v).toString();
  }

  function dhd(load: DatabaseLoad): string | null {
    const v = dhdNum(load);
    return v === Infinity ? null : Math.round(v).toString();
  }

  /* ── Picks display ──────────────────────────────────────────── */
  function picksLabel(loadId: string): string {
    const sc = stopCounts[loadId];
    if (!sc) return "—";
    return `${sc.pickups}p${sc.drops}d`;
  }

  /* ── Sorting ────────────────────────────────────────────────── */
  const sortedLoads = useMemo(() => {
    const arr = [...loads];
    const cmp = (a: DatabaseLoad, b: DatabaseLoad): number => {
      switch (sortBy) {
        case "age_newest":
          return new Date(b.posted_at || "").getTime() - new Date(a.posted_at || "").getTime();
        case "age_oldest":
          return new Date(a.posted_at || "").getTime() - new Date(b.posted_at || "").getTime();
        case "rate_highest":
          return (b.rate_amount ?? 0) - (a.rate_amount ?? 0);
        case "rate_lowest":
          return (a.rate_amount ?? 0) - (b.rate_amount ?? 0);
        case "rate_per_mile_highest": {
          const rpmA = a.total_miles > 0 && a.rate_amount > 0 ? a.rate_amount / a.total_miles : 0;
          const rpmB = b.total_miles > 0 && b.rate_amount > 0 ? b.rate_amount / b.total_miles : 0;
          return rpmB - rpmA;
        }
        case "pickup_soonest":
          return (a.pickup_date || "9999").localeCompare(b.pickup_date || "9999");
        case "pickup_latest":
          return (b.pickup_date || "0000").localeCompare(a.pickup_date || "0000");
        case "trip_shortest":
          return (a.total_miles ?? Infinity) - (b.total_miles ?? Infinity);
        case "trip_longest":
          return (b.total_miles ?? 0) - (a.total_miles ?? 0);
        case "dh_o_shortest":
          return dhoNum(a) - dhoNum(b);
        case "dh_o_longest":
          return dhoNum(b) - dhoNum(a);
        case "dh_d_shortest":
          return dhdNum(a) - dhdNum(b);
        case "dh_d_longest":
          return dhdNum(b) - dhdNum(a);
        case "weight_heaviest":
          return (b.weight_lbs ?? 0) - (a.weight_lbs ?? 0);
        case "weight_lightest":
          return (a.weight_lbs ?? 0) - (b.weight_lbs ?? 0);
        case "length_longest":
          return (b.length_ft ?? 0) - (a.length_ft ?? 0);
        case "length_shortest":
          return (a.length_ft ?? 0) - (b.length_ft ?? 0);
        case "origin_az":
          return (a.origin_city || "").localeCompare(b.origin_city || "");
        case "origin_za":
          return (b.origin_city || "").localeCompare(a.origin_city || "");
        case "dest_az":
          return (a.destination_city || "").localeCompare(b.destination_city || "");
        case "dest_za":
          return (b.destination_city || "").localeCompare(a.destination_city || "");
        case "capacity_highest":
          return getCapacityRank(b.load_type) - getCapacityRank(a.load_type);
        case "capacity_lowest":
          return getCapacityRank(a.load_type) - getCapacityRank(b.load_type);
        case "picks_most": {
          const totalA = (stopCounts[a.id]?.pickups ?? 0) + (stopCounts[a.id]?.drops ?? 0);
          const totalB = (stopCounts[b.id]?.pickups ?? 0) + (stopCounts[b.id]?.drops ?? 0);
          return totalB - totalA;
        }
        case "picks_fewest": {
          const totalA = (stopCounts[a.id]?.pickups ?? 0) + (stopCounts[a.id]?.drops ?? 0);
          const totalB = (stopCounts[b.id]?.pickups ?? 0) + (stopCounts[b.id]?.drops ?? 0);
          return totalA - totalB;
        }
        case "eq_az":
          return (a.equipment_type || "").localeCompare(b.equipment_type || "");
        case "eq_za":
          return (b.equipment_type || "").localeCompare(a.equipment_type || "");
        case "market_az":
          return (a.rate_type || "").localeCompare(b.rate_type || "");
        case "market_za":
          return (b.rate_type || "").localeCompare(a.rate_type || "");
        case "company_az":
          return ((a as any).companies?.name || "").localeCompare((b as any).companies?.name || "");
        case "company_za":
          return ((b as any).companies?.name || "").localeCompare((a as any).companies?.name || "");
        default:
          return 0;
      }
    };
    arr.sort(cmp);
    return arr;
  }, [loads, sortBy, searchOriginCoords, searchDestCoords, stopCounts]);

  /* ── Sort popover state ─────────────────────────────────────── */
  const [sortOpen, setSortOpen] = useState(false);

  /* ── Show skeleton when loading with no existing results ─── */
  const showSkeleton = isLoading && loads.length === 0;

  /* ── Empty state (not loading, no results) ─────────────────── */
  if (!isLoading && loads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
        <Truck className="h-8 w-8 opacity-30" />
        <span className="text-sm">No loads found. Adjust your search filters.</span>
      </div>
    );
  }

  const COL_SPAN = 15;

  return (
    <div className="data-table rounded-lg border border-border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
      <style>{`
        @keyframes newLoadPulse {
          0% { background-color: hsl(142 71% 45% / 0.1); }
          100% { background-color: transparent; }
        }
        .animate-new-load { animation: newLoadPulse 1.5s ease-out forwards; }
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
        .col-sep { position: relative; }
        .col-sep::after {
          content: '';
          position: absolute;
          right: 0;
          top: 20%;
          height: 60%;
          width: 1px;
          background: hsl(var(--border));
          opacity: 0.8;
        }
      `}</style>
      <div className="px-3 py-px border-b bg-muted/20 flex items-center gap-3">
        <span className="text-xs font-semibold text-muted-foreground">
          {showSkeleton ? "Searching…" : `${sortedLoads.length} Results`}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sort:</span>
          <Popover open={sortOpen} onOpenChange={setSortOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 text-xs font-medium text-foreground bg-muted/40 hover:bg-muted border border-border rounded-md px-2.5 py-1 transition-colors">
                {SORT_LABEL_MAP[sortBy] ?? "Age — Newest"}
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start" sideOffset={4}>
              <Command>
                <CommandList className="max-h-[360px]">
                  {SORT_GROUPS.map((group) => (
                    <CommandGroup key={group.heading} heading={group.heading}>
                      {group.options.map(({ key, def }) => (
                        <CommandItem
                          key={key}
                          value={key}
                          onSelect={() => {
                            onSortChange?.(key);
                            setSortOpen(false);
                          }}
                          className="text-xs cursor-pointer"
                        >
                          <Check className={cn("mr-2 h-3 w-3", sortBy === key ? "opacity-100" : "opacity-0")} />
                          {def.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {/* ── Progress bar ── */}
      {isLoading && (
        <div className="h-0.5 w-full overflow-hidden">
          <Progress value={75} className="h-0.5 rounded-none [&>div]:animate-[indeterminate_1.5s_ease-in-out_infinite]" />
        </div>
      )}
      {!isLoading && <div className="h-0.5" />}

      {/* ── Mobile card list (presentation only — same data, same expand handler) ── */}
      <div className="md:hidden flex flex-col gap-2 p-3">
        {sortedLoads.map((load) => {
          const age = formatAge(load.posted_at || "");
          let effectiveMiles = load.total_miles && load.total_miles > 0 ? load.total_miles : 0;
          let isEstimatedMiles = false;
          if (effectiveMiles === 0 && load.origin_latitude && load.origin_longitude && load.destination_latitude && load.destination_longitude) {
            effectiveMiles = Math.round(haversineDistance(load.origin_latitude, load.origin_longitude, load.destination_latitude, load.destination_longitude) * 1.2);
            isEstimatedMiles = effectiveMiles > 0;
          }
          const ratePerMile = effectiveMiles > 0 && load.rate_amount > 0 ? load.rate_amount / effectiveMiles : 0;
          const equipLabel = load.equipment_type ? formatEquipmentType(load.equipment_type) : "—";
          const isFading = fadingLoadIds?.has(load.id);
          const isExpanded = expandedLoadId === load.id;
          return (
            <React.Fragment key={load.id}>
              <button
                type="button"
                disabled={isFading}
                onClick={() => {
                  if (isFading) return;
                  setExpandedLoadId(isExpanded ? null : load.id);
                }}
                className={cn(
                  "text-left rounded-lg border border-border bg-card p-3 min-w-0",
                  "active:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-teal-deep",
                  isFading && "opacity-40 cursor-default",
                  isExpanded && !isFading && "border-l-2 border-l-brand-teal bg-brand-teal-subtle/30",
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <span className="text-sm font-semibold truncate min-w-0">
                    {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
                  </span>
                  <span className={cn("text-[10px] font-semibold whitespace-nowrap shrink-0", getAgeText(age.mins))}>
                    {age.text}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="truncate min-w-0">
                    {equipLabel}
                    {load.pickup_date && <> · {formatYYYYMMDDToLocaleDateString(load.pickup_date)}</>}
                    {effectiveMiles > 0 && <> · {effectiveMiles}mi</>}
                  </span>
                  <span className="font-semibold text-foreground shrink-0">
                    {load.rate_amount ? `$${load.rate_amount.toLocaleString()}` : "Call"}
                    {ratePerMile > 0 && <span className="ml-1 text-[10px] text-muted-foreground">${ratePerMile.toFixed(2)}/mi</span>}
                  </span>
                </div>
              </button>
              {isExpanded && (
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <LoadDetailsPanel load={{
                    id: parseInt(load.id.slice(0, 8), 16) || 0,
                    loadId: load.id,
                    ageMinutes: age.mins,
                    pickupDate: load.pickup_date ? formatYYYYMMDDToLocaleDateString(load.pickup_date, "en-US", { month: "short", day: "numeric" }) : "—",
                    dropDate: load.delivery_date ? formatYYYYMMDDToLocaleDateString(load.delivery_date, "en-US", { month: "short", day: "numeric" }) : "—",
                    originCity: load.origin_city,
                    originState: load.origin_state,
                    destCity: load.destination_city,
                    destState: load.destination_state,
                    pickups: stopCounts[load.id]?.pickups || 1,
                    drops: stopCounts[load.id]?.drops || 1,
                    type: (load.load_type as string) || "FTL",
                    rate: load.rate_amount || 0,
                    weight: load.weight_lbs || 0,
                    miles: effectiveMiles,
                    isEstimatedMiles,
                    equipment: load.equipment_type,
                    ratePerMile,
                    company: displayCompanyName(load.companies?.name) || "—",
                    companyId: load.company_id || null,
                    createdBy: load.created_by,
                    contactName: load.contact_name,
                    contactPhone: load.contact_phone || null,
                    contactPhoneSecondary: load.contact_phone_secondary || null,
                    contactEmail: load.contact_email || null,
                    lengthFt: load.length_ft || null,
                    commodity: load.commodity || null,
                    specialInstructions: load.special_instructions || null,
                    referenceNumber: load.reference_number || null,
                    loadNumber: load.load_number || null,
                    paymentTerms: load.payment_terms || null,
                    pieces: load.pieces || null,
                    fuelSurchargeRate: load.fuel_surcharge_rate || null,
                    accessorialCharges: load.accessorial_charges || null,
                    originLat: load.origin_latitude ? Number(load.origin_latitude) : null,
                    originLon: load.origin_longitude ? Number(load.origin_longitude) : null,
                    destLat: load.destination_latitude ? Number(load.destination_latitude) : null,
                    destLon: load.destination_longitude ? Number(load.destination_longitude) : null,
                    dbEquipmentType: load.equipment_type,
                    briefAudioPath: load.brief_audio_path ?? null,
                    briefDurationSeconds: load.brief_duration_seconds ?? null,
                  }} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Desktop table (≥ md) — unchanged markup, just gated ── */}
      <div className="hidden md:block">
      <Table>
        <TableHeader className="sticky top-0 z-10">
           <TableRow className="bg-muted/40 hover:bg-muted/40 border-b border-border">
            <SortableTH className="w-[88px]" sortKey="age" currentSort={sortBy} onSort={onSortChange!}>Age</SortableTH>
            <SortableTH className="text-right min-w-[90px] col-sep" sortKey="rate" currentSort={sortBy} onSort={onSortChange!}>Rate</SortableTH>
            <SortableTH className="min-w-[120px] col-sep" sortKey="origin" currentSort={sortBy} onSort={onSortChange!}>Origin</SortableTH>
            <SortableTH className="text-right w-[50px]" sortKey="dho" currentSort={sortBy} onSort={onSortChange!}>DH-O</SortableTH>
            <SortableTH className="min-w-[120px] col-sep" sortKey="dest" currentSort={sortBy} onSort={onSortChange!}>Dest</SortableTH>
            <SortableTH className="text-right w-[50px]" sortKey="dhd" currentSort={sortBy} onSort={onSortChange!}>DH-D</SortableTH>
            <SortableTH className="min-w-[72px] col-sep" sortKey="pickup" currentSort={sortBy} onSort={onSortChange!}>Pick Up</SortableTH>
            <SortableTH className="text-center w-[50px]" sortKey="picks" currentSort={sortBy} onSort={onSortChange!}>Picks</SortableTH>
            <SortableTH className="text-center w-[42px]" sortKey="eq" currentSort={sortBy} onSort={onSortChange!}>EQ</SortableTH>
            <SortableTH className="text-right w-[56px]" sortKey="length" currentSort={sortBy} onSort={onSortChange!}>Len</SortableTH>
            <SortableTH className="text-right min-w-[64px] col-sep" sortKey="weight" currentSort={sortBy} onSort={onSortChange!}>WT</SortableTH>
            <SortableTH className="text-center w-[50px]" sortKey="capacity" currentSort={sortBy} onSort={onSortChange!}>Cap</SortableTH>
            <SortableTH className="text-center w-[55px]" sortKey="market" currentSort={sortBy} onSort={onSortChange!}>Mkt</SortableTH>
            <SortableTH className="max-w-[120px] col-sep" sortKey="company" currentSort={sortBy} onSort={onSortChange!}>Company</SortableTH>
            <TH className="max-w-[100px]">Contact</TH>
          </TableRow>
        </TableHeader>
        <TableBody>
          {showSkeleton && Array.from({ length: 8 }).map((_, i) => (
            <TableRow key={`skel-${i}`} className={cn(i % 2 === 0 ? "bg-background" : "bg-slate-50")}>
              <TableCell className="px-2.5 py-1"><Skeleton className="h-5 w-14 rounded-md" /></TableCell>
              <TableCell className="px-2.5 py-1 text-right col-sep"><Skeleton className="h-5 w-16 ml-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 col-sep"><Skeleton className="h-4 w-24 rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-right"><Skeleton className="h-4 w-8 ml-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 col-sep"><Skeleton className="h-4 w-24 rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-right"><Skeleton className="h-4 w-8 ml-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 col-sep"><Skeleton className="h-4 w-16 rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-center"><Skeleton className="h-4 w-5 mx-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-center"><Skeleton className="h-4 w-8 mx-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-right"><Skeleton className="h-4 w-8 ml-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-right col-sep"><Skeleton className="h-4 w-12 ml-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-center"><Skeleton className="h-4 w-8 mx-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 text-center"><Skeleton className="h-4 w-8 mx-auto rounded" /></TableCell>
              <TableCell className="px-2.5 py-1 col-sep"><Skeleton className="h-4 w-20 rounded" /></TableCell>
              <TableCell className="px-2.5 py-1"><Skeleton className="h-4 w-16 rounded" /></TableCell>
            </TableRow>
          ))}
          {!showSkeleton && sortedLoads.map((load, i) => {
            const age = formatAge(load.posted_at || "");
            // RPM: prefer total_miles, fallback to haversine estimate
            let effectiveMiles = load.total_miles && load.total_miles > 0 ? load.total_miles : 0;
            let isEstimatedMiles = false;
            if (effectiveMiles === 0 && load.origin_latitude && load.origin_longitude && load.destination_latitude && load.destination_longitude) {
              effectiveMiles = Math.round(haversineDistance(load.origin_latitude, load.origin_longitude, load.destination_latitude, load.destination_longitude) * 1.2);
              isEstimatedMiles = effectiveMiles > 0;
            }
            const ratePerMile = effectiveMiles > 0 && load.rate_amount > 0 ? load.rate_amount / effectiveMiles : 0;
            const equipLabel = load.equipment_type ? formatEquipmentType(load.equipment_type) : "—";
            const isExpanded = expandedLoadId === load.id;
            const isFading = fadingLoadIds?.has(load.id);
            const fadeStatus = isFading ? fadingLoadIds!.get(load.id)! : null;
            const isNew = newLoadIds?.has(load.id);
            const dhOVal = dho(load);
            const dhDVal = dhd(load);
            const isLaneMatch = dhoNum(load) <= 0 && dhdNum(load) <= 0;

            return (
              <React.Fragment key={load.id}>
                <TableRow
                  key={load.id}
                  id={`load-row-${load.id}`}
                  onClick={() => {
                    if (isFading) return; // Greyed-out loads are not clickable
                    const wasExpanded = expandedLoadId === load.id;
                    setExpandedLoadId(wasExpanded ? null : load.id);
                  }}
                  className={cn(
                    "group transition-colors duration-200",
                    isFading ? "opacity-40 bg-muted/30 cursor-default" : "cursor-pointer hover:bg-brand-teal/20",
                    !isFading && (i % 2 === 0 ? "bg-background" : "bg-slate-50"),
                    isLaneMatch && !isFading && !isNew && !isExpanded && "border-l-[3px] border-l-brand-teal",
                    isExpanded && !isFading && "border-l-2 border-l-brand-teal bg-brand-teal-subtle/30",
                    isNew && !isFading && "border-l-2 border-l-green-500 animate-new-load",
                    highlightedLoadId === load.id && "ring-2 ring-brand-teal ring-inset",
                  )}
                >
                  {/* Age */}
                  <TableCell className="px-2.5 py-1">
                    <div className="flex items-center gap-1.5">
                      <span className={cn("font-semibold whitespace-nowrap", getAgeText(age.mins))} style={{ fontSize: '0.55rem' }}>{age.text}</span>
                      {fadeStatus && (
                        <Badge variant="destructive" className="text-[9px] px-1.5 py-0 h-4 font-semibold">
                          {statusBadgeLabel(fadeStatus)}
                        </Badge>
                      )}
                    </div>
                  </TableCell>

                  {/* Rate */}
                  <TableCell className="text-right px-2.5 py-1 col-sep">
                    <div className="flex flex-col items-end leading-tight">
                    <span className="text-[12px] font-semibold" style={{ color: '#192129' }}>
                        {load.rate_amount ? `$${load.rate_amount.toLocaleString()}` : "—"}
                      </span>
                      {ratePerMile > 0 && (
                        <span className="text-gray-600" style={{ fontSize: '0.55rem', fontWeight: 200, lineHeight: '1rem' }}>${ratePerMile.toFixed(2)}/mi</span>
                      )}
                    </div>
                  </TableCell>

                  {/* Origin */}
                  <TableCell className="px-2.5 py-1 whitespace-nowrap col-sep">
                    <span className={cn("text-[12px] font-medium", isFading ? "line-through text-muted-foreground" : "text-foreground")}>{load.origin_city},</span>
                    <span className="text-[12px] text-gray-600 ml-0.5">{load.origin_state}</span>
                  </TableCell>

                  {/* DH-O */}
                  <TableCell className="text-right px-2 py-1">
                    <span className="text-[11px] font-medium text-muted-foreground font-mono">{dhOVal ?? "—"}</span>
                  </TableCell>

                  {/* Destination */}
                  <TableCell className="px-2.5 py-1 whitespace-nowrap col-sep">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground/60">→</span>
                      <span className={cn("text-[12px] font-medium", isFading ? "line-through text-muted-foreground" : "text-foreground")}>{load.destination_city},</span>
                      <span className="text-[12px] text-gray-600">{load.destination_state}</span>
                    </div>
                  </TableCell>

                  {/* DH-D */}
                  <TableCell className="text-right px-2 py-1">
                    <span className="text-[11px] font-medium text-muted-foreground font-mono">{dhDVal ?? "—"}</span>
                  </TableCell>

                  {/* Pick Up */}
                  <TableCell className="px-2.5 py-1 col-sep">
                    <span className="text-[12px] text-foreground">
                      {load.pickup_date ? formatYYYYMMDDToLocaleDateString(load.pickup_date, "en-US", { month: "short", day: "numeric" }) : "—"}
                    </span>
                  </TableCell>

                  {/* Picks */}
                  <TableCell className="text-center px-2 py-1">
                    <span className="text-[12px] font-medium font-mono text-muted-foreground">{picksLabel(load.id)}</span>
                  </TableCell>

                  {/* EQ */}
                  <TableCell className="text-center px-2 py-1">
                    <span className="text-[12px] font-medium text-muted-foreground/75">{equipLabel}</span>
                  </TableCell>

                  {/* Length */}
                  <TableCell className="text-right px-2 py-1">
                    <span className="text-[12px] text-muted-foreground/75 font-mono">
                      {load.length_ft ? `${load.length_ft} ft` : "—"}
                    </span>
                  </TableCell>

                  {/* Weight */}
                  <TableCell className="text-right px-2.5 py-1 whitespace-nowrap col-sep">
                    <span className="text-[13px] font-medium text-foreground font-mono">
                      {formatWeight(load.weight_lbs)}
                    </span>
                  </TableCell>

                  {/* Capacity */}
                  <TableCell className="text-center px-2 py-1">
                    <span className="text-[12px] font-medium text-muted-foreground/75">
                      {load.load_type === "FTL" ? "Full" : load.load_type === "LTL" ? "Partial" : "—"}
                    </span>
                  </TableCell>

                  {/* Market */}
                  <TableCell className="text-center px-2 py-1">
                    <span className="text-[12px] text-muted-foreground/75">
                      {load.rate_type === "spot" ? "Spot" : load.rate_type === "contract" ? "Contract" : "—"}
                    </span>
                  </TableCell>

                  {/* Company */}
                  <TableCell className="px-2.5 py-1 col-sep">
                    <span className="text-[13px] font-semibold text-brand-teal-deep cursor-pointer hover:underline truncate max-w-[120px] inline-block">
                      {displayCompanyName(load.companies?.name) || "—"}
                    </span>
                  </TableCell>

                  {/* Contact */}
                  <TableCell className="px-2 py-1">
                    <span className="text-[13px] font-semibold text-brand-teal-deep truncate max-w-[120px] inline-block">
                      {load.contact_phone ? formatPhoneNumber(load.contact_phone) : (load.contact_name || "—")}
                    </span>
                  </TableCell>

                </TableRow>

                {isExpanded && (
                  <TableRow key={`${load.id}-details`} className="hover:bg-transparent border-0">
                    <TableCell colSpan={COL_SPAN} className="p-0 border-0">
                      <LoadDetailsPanel load={{
                        id: parseInt(load.id.slice(0, 8), 16) || 0,
                        loadId: load.id,
                        ageMinutes: age.mins,
                        pickupDate: load.pickup_date ? formatYYYYMMDDToLocaleDateString(load.pickup_date, "en-US", { month: "short", day: "numeric" }) : "—",
                        dropDate: load.delivery_date ? formatYYYYMMDDToLocaleDateString(load.delivery_date, "en-US", { month: "short", day: "numeric" }) : "—",
                        originCity: load.origin_city,
                        originState: load.origin_state,
                        destCity: load.destination_city,
                        destState: load.destination_state,
                        pickups: stopCounts[load.id]?.pickups || 1,
                        drops: stopCounts[load.id]?.drops || 1,
                        type: (load.load_type as string) || "FTL",
                        rate: load.rate_amount || 0,
                        weight: load.weight_lbs || 0,
                        miles: effectiveMiles,
                        isEstimatedMiles,
                        equipment: load.equipment_type,
                        ratePerMile,
                        company: displayCompanyName(load.companies?.name) || "—",
                        companyId: load.company_id || null,
                        createdBy: load.created_by,
                        contactName: load.contact_name,
                        contactPhone: load.contact_phone || null,
                        contactPhoneSecondary: load.contact_phone_secondary || null,
                        contactEmail: load.contact_email || null,
                        lengthFt: load.length_ft || null,
                        commodity: load.commodity || null,
                        specialInstructions: load.special_instructions || null,
                        referenceNumber: load.reference_number || null,
                        loadNumber: load.load_number || null,
                        paymentTerms: load.payment_terms || null,
                        pieces: load.pieces || null,
                        fuelSurchargeRate: load.fuel_surcharge_rate || null,
                        accessorialCharges: load.accessorial_charges || null,
                        originLat: load.origin_latitude ? Number(load.origin_latitude) : null,
                        originLon: load.origin_longitude ? Number(load.origin_longitude) : null,
                        destLat: load.destination_latitude ? Number(load.destination_latitude) : null,
                        destLon: load.destination_longitude ? Number(load.destination_longitude) : null,
                        dbEquipmentType: load.equipment_type,
                        briefAudioPath: load.brief_audio_path ?? null,
                        briefDurationSeconds: load.brief_duration_seconds ?? null,
                      }} />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
